import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { reportService } from '../services/reportService';
import { customerService } from '../services/customerService';
import { formatCurrency } from '../components/CurrencyTextField';
import ReportExportMenu from '../components/ReportExportMenu';

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  const ymd = (d) => d.toISOString().slice(0, 10);
  return { dateFrom: ymd(start), dateTo: ymd(end) };
}

const SalesReport = () => {
  const navigate = useNavigate();
  const defaults = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [customerFilter, setCustomerFilter] = useState('');
  const [groupBy, setGroupBy] = useState('order');
  const [includeUninvoicedOrders, setIncludeUninvoicedOrders] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    customerService.getAll({ limit: 500 }).then((r) => setCustomers(r?.data ?? [])).catch(() => setCustomers([]));
  }, []);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        group_by: groupBy
      };
      if (customerFilter) params.customer_id = customerFilter;
      if (includeUninvoicedOrders) params.include_uninvoiced_orders = true;
      const res = await reportService.getSalesSummary(params);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load sales report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, customerFilter, groupBy, includeUninvoicedOrders]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const rows = data?.rows ?? [];
  const totals = data?.totals ?? null;
  const reportIncludesUninvoiced = Boolean(data?.include_uninvoiced_orders);
  const usesSubColumns = groupBy === 'invoice' || groupBy === 'order';
  const showBalanceDueCol = usesSubColumns;

  const customerName = (c) =>
    c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`;

  const firstColHeader = () => {
    switch (groupBy) {
      case 'customer':
        return 'Customer';
      case 'day':
        return reportIncludesUninvoiced ? 'Date' : 'Invoice date';
      case 'month':
        return 'Month';
      default:
        return '';
    }
  };

  const moneyOrDash = (v) => (v == null || v === '' ? '-' : formatCurrency(v));

  /** Order-level amounts repeat on each invoice row for the same work order; show once and use em dash on repeats. */
  const orderLevelMoney = (row, v) =>
    row.same_work_order_repeat ? '—' : formatCurrency(v);

  const renderFirstCell = (row) => {
    if (groupBy === 'customer') {
      const name = row.company_name || `#${row.customer_id}`;
      return (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => navigate(`/customers/${row.customer_id}`)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {name}
        </Link>
      );
    }
    if (groupBy === 'day' && row.period_date) {
      return format(new Date(row.period_date), 'MM/dd/yyyy');
    }
    if (groupBy === 'month' && row.period_start) {
      return format(new Date(row.period_start), 'MMM yyyy');
    }
    return '—';
  };

  const orderCol = {
    key: 'order',
    label: 'Order',
    render: (r) => {
      const label = r.work_order_identifier ?? r.work_order_number ?? '—';
      if (r.work_order_id == null) return label;
      return (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => navigate(`/work-orders/${r.work_order_id}`)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {label}
        </Link>
      );
    }
  };

  const customerCol = {
    key: 'customer',
    label: 'Customer',
    render: (r) =>
      r.company_name ||
      [r.first_name, r.last_name].filter(Boolean).join(' ') ||
      (r.customer_id ? `#${r.customer_id}` : '—')
  };

  const subCols =
    groupBy === 'invoice'
      ? [
          orderCol,
          {
            key: 'invoice',
            label: 'Invoice',
            render: (r) => {
              if (r.has_invoice === false) return '-';
              const label = r.invoice_identifier ?? r.invoice_number ?? `#${r.invoice_id}`;
              return (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate(`/customer-invoices/${r.invoice_id}`)}
                  sx={{ cursor: 'pointer', textAlign: 'left' }}
                >
                  {label}
                </Link>
              );
            }
          },
          customerCol
        ]
      : groupBy === 'order'
        ? [orderCol, customerCol]
        : [];

  const labelColSpan = usesSubColumns ? subCols.length : 1;

  const moneyOrDashText = (v) => (v == null || v === '' ? '-' : formatCurrency(v));

  const firstColExportText = (row) => {
    if (groupBy === 'customer') {
      return row.company_name || `#${row.customer_id}`;
    }
    if (groupBy === 'day' && row.period_date) {
      return format(new Date(row.period_date), 'MM/dd/yyyy');
    }
    if (groupBy === 'month' && row.period_start) {
      return format(new Date(row.period_start), 'MMM yyyy');
    }
    return '—';
  };

  const amountCells = (row) => {
    const base = [
      orderLevelMoney(row, row.order_total),
      orderLevelMoney(row, row.shipped_total),
      moneyOrDashText(row.shipping),
      moneyOrDashText(row.tax),
      moneyOrDashText(row.invoice_total),
      orderLevelMoney(row, row.uninvoiced_amount),
      formatCurrency(row.unpaid_amount)
    ];
    if (showBalanceDueCol) {
      base.push(row.balance_due == null ? '—' : orderLevelMoney(row, row.balance_due));
    }
    return base;
  };

  const getSalesExportData = useCallback(() => {
    if (!rows.length) return null;
    const amountHeaders = [
      'Order total',
      'Shipped total',
      'Shipping',
      'Tax',
      'Invoice total',
      'UnInvoiced',
      'Unpaid invoices',
      ...(showBalanceDueCol ? ['Balance due'] : [])
    ];
    let headers;
    let body;
    let footer;
    if (groupBy === 'invoice') {
      headers = ['Order', 'Invoice', 'Customer', ...amountHeaders];
      body = rows.map((r) => [
        r.work_order_identifier ?? r.work_order_number ?? '—',
        r.has_invoice === false ? '-' : String(r.invoice_identifier ?? r.invoice_number ?? (r.invoice_id != null ? `#${r.invoice_id}` : '—')),
        r.company_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.customer_id ? `#${r.customer_id}` : '—'),
        ...amountCells(r)
      ]);
      if (totals) {
        footer = [
          'Totals',
          '',
          '',
          formatCurrency(totals.order_total),
          formatCurrency(totals.shipped_total),
          formatCurrency(totals.shipping),
          formatCurrency(totals.tax),
          formatCurrency(totals.invoice_total),
          formatCurrency(totals.uninvoiced_amount),
          formatCurrency(totals.unpaid_amount),
          totals.balance_due != null ? formatCurrency(totals.balance_due) : '—'
        ];
      }
    } else if (groupBy === 'order') {
      headers = ['Order', 'Customer', ...amountHeaders];
      body = rows.map((r) => [
        r.work_order_identifier ?? r.work_order_number ?? '—',
        r.company_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.customer_id ? `#${r.customer_id}` : '—'),
        ...amountCells(r)
      ]);
      if (totals) {
        footer = [
          'Totals',
          '',
          formatCurrency(totals.order_total),
          formatCurrency(totals.shipped_total),
          formatCurrency(totals.shipping),
          formatCurrency(totals.tax),
          formatCurrency(totals.invoice_total),
          formatCurrency(totals.uninvoiced_amount),
          formatCurrency(totals.unpaid_amount),
          totals.balance_due != null ? formatCurrency(totals.balance_due) : '—'
        ];
      }
    } else {
      headers = [firstColHeader(), ...amountHeaders];
      body = rows.map((r) => [firstColExportText(r), ...amountCells(r)]);
      if (totals) {
        footer = [
          'Totals',
          formatCurrency(totals.order_total),
          formatCurrency(totals.shipped_total),
          formatCurrency(totals.shipping),
          formatCurrency(totals.tax),
          formatCurrency(totals.invoice_total),
          formatCurrency(totals.uninvoiced_amount),
          formatCurrency(totals.unpaid_amount)
        ];
      }
    }
    const groupLabel =
      groupBy === 'invoice'
        ? 'Invoice (detail)'
        : groupBy === 'order'
          ? 'Order'
          : groupBy === 'customer'
            ? 'Customer'
            : groupBy === 'day'
              ? 'Day'
              : 'Month';
    const cust = customerFilter
      ? customerName(customers.find((c) => String(c.id) === String(customerFilter)) || {})
      : 'All customers';
    const pdfTitle = `Sales summary · ${dateFrom} – ${dateTo} · ${groupLabel} · ${cust}${
      includeUninvoicedOrders ? ' · incl. uninvoiced orders' : ''
    }`;
    const filenameBase = `sales-summary_${dateFrom}_${dateTo}_${groupBy}`;
    return { headers, rows: body, footer: footer || null, pdfTitle, filenameBase, pdfLandscape: true };
  }, [
    rows,
    totals,
    groupBy,
    customerFilter,
    customers,
    dateFrom,
    dateTo,
    includeUninvoicedOrders,
    reportIncludesUninvoiced,
    firstColHeader,
    firstColExportText,
    amountCells,
    customerName
  ]);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 0.5 }}>
        Sales summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
        Order total is the work order value (line items plus order-level tax). Shipped total is the sum of customer packing slip
        lines (quantity shipped × work order unit price). Invoice shipping and tax come from the invoice. UnInvoiced is order total
        minus invoiced line amounts and invoice tax (not invoice shipping); it stays at zero when the order is fully invoiced. Unpaid
        invoices is this invoice’s outstanding amount (invoice total minus amount paid) in invoice detail, or the sum of those amounts
        across all invoices on the work order when grouped by order. Balance due is the uninvoiced gap plus unpaid invoice balances
        on that work order. In invoice detail, order total, shipped total, UnInvoiced, and balance due are shown once per work order
        when multiple invoices share the same order.
        {includeUninvoicedOrders
          ? ' Rows without an invoice use the order date for the date filter; invoice shipping and tax show as "-".'
          : ''}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Customer"
            >
              <MenuItem value="">All</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {customerName(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Group by</InputLabel>
            <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} label="Group by">
              <MenuItem value="invoice">Invoice (detail)</MenuItem>
              <MenuItem value="order">Order</MenuItem>
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeUninvoicedOrders}
                onChange={(e) => setIncludeUninvoicedOrders(e.target.checked)}
              />
            }
            label="Include orders without invoices"
          />
          <Button size="small" variant="outlined" onClick={loadReport}>
            Refresh
          </Button>
          <ReportExportMenu
            disabled={loading || rows.length === 0}
            getData={getSalesExportData}
            defaultFilenameBase="sales-summary"
          />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!usesSubColumns && <TableCell>{firstColHeader()}</TableCell>}
                {usesSubColumns &&
                  subCols.map((c) => (
                    <TableCell key={c.key}>{c.label}</TableCell>
                  ))}
                <TableCell align="right">Order total</TableCell>
                <TableCell align="right">Shipped total</TableCell>
                <TableCell align="right">Shipping</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Invoice total</TableCell>
                <TableCell align="right">UnInvoiced</TableCell>
                <TableCell align="right">Unpaid invoices</TableCell>
                {showBalanceDueCol && <TableCell align="right">Balance due</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow
                  key={
                    groupBy === 'order' && row.work_order_id != null
                      ? `order-${row.work_order_id}`
                      : row.invoice_id != null
                        ? `inv-${row.invoice_id}`
                        : row.work_order_id != null
                          ? `wo-${row.work_order_id}-u`
                          : `${row.customer_id}-${row.period_date}-${row.period_start}-${i}`
                  }
                  hover
                >
                  {!usesSubColumns && <TableCell>{renderFirstCell(row)}</TableCell>}
                  {usesSubColumns &&
                    subCols.map((c) => (
                      <TableCell key={c.key}>{c.render(row)}</TableCell>
                    ))}
                  <TableCell align="right" title={row.same_work_order_repeat ? 'Counted once per work order (first row)' : ''}>
                    {orderLevelMoney(row, row.order_total)}
                  </TableCell>
                  <TableCell align="right" title={row.same_work_order_repeat ? 'Counted once per work order (first row)' : ''}>
                    {orderLevelMoney(row, row.shipped_total)}
                  </TableCell>
                  <TableCell align="right">{moneyOrDash(row.shipping)}</TableCell>
                  <TableCell align="right">{moneyOrDash(row.tax)}</TableCell>
                  <TableCell align="right">{moneyOrDash(row.invoice_total)}</TableCell>
                  <TableCell align="right" title={row.same_work_order_repeat ? 'Counted once per work order (first row)' : ''}>
                    {orderLevelMoney(row, row.uninvoiced_amount)}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(row.unpaid_amount)}</TableCell>
                  {showBalanceDueCol && (
                    <TableCell align="right" title={row.same_work_order_repeat ? 'Same work order as row above' : ''}>
                      {row.balance_due == null ? '—' : orderLevelMoney(row, row.balance_due)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {totals && (
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell colSpan={labelColSpan}>Totals</TableCell>
                  <TableCell align="right">{formatCurrency(totals.order_total)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.shipped_total)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.shipping)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.tax)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.invoice_total)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.uninvoiced_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.unpaid_amount)}</TableCell>
                  {showBalanceDueCol && (
                    <TableCell align="right">
                      {totals.balance_due != null ? formatCurrency(totals.balance_due) : '—'}
                    </TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && rows.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {includeUninvoicedOrders ? 'No invoices or orders in this range.' : 'No invoices in this range.'}
        </Typography>
      )}
    </Box>
  );
};

export default SalesReport;
